import React from 'react';
import { Flex, Heading, Text, Image } from 'rebass';
import styled from 'styled-components';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RECRUITER, EMPLOYER, ACTIVE } from 'constants/index';
import Link from 'components/Link';
import SkillBox from 'components/SkillBox';
import bookmarkIcon from 'assets/icons/bookmark.png';
import filledBookmarkIcon from 'assets/icons/filledBookmark.png';
import verifiedIcon from 'assets/icons/verified.png';
import pendingIcon from 'assets/icons/pending.png';
import candidateIcon from 'assets/icons/candidate.png';
import colors from 'theme/colors';
import Tooltip, { TooltipText } from 'components/Tooltip';
import useJobPostNavigation from '../hooks/useJobPostNavigation';

const JobPostWrapper = styled(Flex)`
  border: 1px solid ${colors.primary};
`;

const JobSkillsWrapper = styled(Flex)`
  border-bottom: 2px solid ${colors.gray};
`;

const BookmarkIcon = styled(Image)<any>`
  cursor: ${props => (props.loadingProp ? 'wait' : 'pointer')};
  opacity: ${props => (props.loadingProp ? '0.5' : '1')};
`;

const JobPost: React.FC<any> = ({ post, userRole, url }): JSX.Element => {
  const {
    id,
    title,
    createdAt,
    city,
    compensation,
    compensationAsRate,
    skills,
    status,
    saved,
    candidateCount,
    companyName,
  } = post;

  const { saveRecruiterJobPostHandler, deleteRecruiterJobPostHandler } = useJobPostNavigation(id);

  const loading = useSelector<any, boolean>(state => state.loading);
  const statusText = status === ACTIVE ? 'Active' : 'Non-Published';

  return (
    <JobPostWrapper width="calc(50% - 30px)" px={1} flexDirection="column" mb={6}>
      <Flex flexDirection="column" py={3} px={1}>
        <Flex justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Flex>
            <Heading
              as="h2"
              fontSize={['22px']}
              fontWeight={6}
              css="
                max-width: 460px;
                overflow: hidden;
              "
            >
              {title}
            </Heading>
          </Flex>
          {userRole === RECRUITER && saved && url === '/job-posts/all' && (
            <BookmarkIcon
              src={filledBookmarkIcon}
              onClick={deleteRecruiterJobPostHandler}
              loadingProp={loading && loading}
            />
          )}
          {userRole === RECRUITER && !saved && url === '/job-posts/all' && (
            <BookmarkIcon
              src={bookmarkIcon}
              onClick={saveRecruiterJobPostHandler}
              loadingProp={loading && loading}
            />
          )}
          {userRole === RECRUITER && +candidateCount > 0 && url === '/job-posts' && (
            <Flex>
              <Image src={candidateIcon} mr={1} />
              <Text fontSize="20px" fontWeight={5}>
                {candidateCount}
              </Text>
            </Flex>
          )}
          {userRole === RECRUITER && saved && !candidateCount && url === '/job-posts' && (
            <BookmarkIcon
              src={filledBookmarkIcon}
              onClick={deleteRecruiterJobPostHandler}
              loadingProp={loading && loading}
            />
          )}
          {userRole === EMPLOYER && (
            <Tooltip>
              <Flex>
                <Image src={status === ACTIVE ? verifiedIcon : pendingIcon} />
              </Flex>
              <TooltipText id="tooltipText" width="150px" padding="0 20px" top="2px" right="40px">
                {status}
              </TooltipText>
            </Tooltip>
          )}
        </Flex>
        {userRole === RECRUITER && (
          <Flex alignItems="center" mb={2}>
            <Heading
              as="h2"
              fontWeight={6}
              fontSize={['18px']}
              css="
              max-width: 460px;
              overflow: hidden;
            "
            >
              {companyName}
            </Heading>
            <Text as="span" fontSize={['18px']} fontWeight={6}>
              &nbsp;-&nbsp;{city}, {post.state}
            </Text>
          </Flex>
        )}
        {userRole === EMPLOYER && (
          <Flex alignItems="center" mb={2}>
            <Text as="span" fontSize={['18px']} fontWeight={6}>
              {city}, {post.state}
            </Text>
          </Flex>
        )}
        {userRole === EMPLOYER && (
          <Text as="p" fontSize={['16px']} fontWeight={6} mb={1}>
            {`Status: ${statusText}`}
          </Text>
        )}
        <Text as="p" fontSize={['16px']} mb={1}>
          {`Posted on: ${format(createdAt, 'MMM DD, YYYY')}`}
        </Text>
        <Text as="p" fontSize={['16px']}>
          {`Compensation for recruiter: ${compensationAsRate ? '' : '$'}${compensation}${
            compensationAsRate ? '%' : ''
          }`}
        </Text>
      </Flex>
      <JobSkillsWrapper justifyContent="center" pb={4}>
        {skills &&
          skills.map((skill: string) => (
            <SkillBox key={skill} mr={1} py={1} px={2}>
              {skill}
            </SkillBox>
          ))}
      </JobSkillsWrapper>
      <Flex justifyContent="center" py={2}>
        {userRole === EMPLOYER && (
          <Link
            to={status === ACTIVE ? `/job-board/${id}` : `/job-post-preview/${id}`}
            color={colors.turquoise}
            hover={colors.turquoise}
            css="font-weight: 500;"
          >
            {status === ACTIVE ? 'View job board' : 'View details'}
          </Link>
        )}
        {userRole === RECRUITER && (
          <Link
            to={url === '/job-posts/all' ? `/job-post-details/${id}` : `/job-board/${id}`}
            color={colors.turquoise}
            hover={colors.turquoise}
            css="font-weight: 500;"
          >
            {url === '/job-posts/all' && 'View details'}
            {url === '/job-posts' && 'View job board'}
          </Link>
        )}
      </Flex>
    </JobPostWrapper>
  );
};

export default JobPost;
